import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import embeddingService from '@/services/embeddingService';
import { quickHybridSearch } from '@/services/hybridRetrieval';
import outcomeTracking from '@/services/outcomeTracking';
import { supabase } from '@/lib/supabase';
import NavigationBar from '@/components/ui/NavigationBar';
import LoadingState from '@/components/ui/LoadingState';
import { Button } from '@/components/ui/Button';

export default function EmbeddingsAdmin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [performanceStats, setPerformanceStats] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadStats();
      loadPerformanceStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      // Get embedding coverage stats
      const { count: totalUnits } = await supabase
        .from('response_units')
        .select('*', { count: 'exact', head: true });

      const { count: unitsWithEmbeddings } = await supabase
        .from('response_units')
        .select('*', { count: 'exact', head: true })
        .not('content_embedding', 'is', null);

      const { count: ruleCount } = await supabase
        .from('response_rules')
        .select('*', { count: 'exact', head: true });

      const { count: outcomeCount } = await supabase
        .from('response_outcomes')
        .select('*', { count: 'exact', head: true });

      setStats({
        totalUnits,
        unitsWithEmbeddings,
        coverage: totalUnits > 0 ? Math.round((unitsWithEmbeddings / totalUnits) * 100) : 0,
        ruleCount,
        outcomeCount
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPerformanceStats = async () => {
    try {
      const topPerformers = await outcomeTracking.getTopPerformers({
        type: 'objection',
        minUses: 3,
        limit: 5
      });

      const lowPerformers = await outcomeTracking.getLowPerformers({
        type: 'objection',
        minUses: 3,
        maxSuccessRate: 50,
        limit: 5
      });

      setPerformanceStats({
        topPerformers,
        lowPerformers
      });
    } catch (error) {
      console.error('Error loading performance stats:', error);
    }
  };

  const handleGenerateEmbeddings = async () => {
    setIsGenerating(true);
    setProgress({ current: 0, total: 0, status: 'starting' });

    try {
      // Fetch response units without embeddings
      const { data: units, error } = await supabase
        .from('response_units')
        .select('*')
        .is('content_embedding', null)
        .eq('is_approved', true);

      if (error) throw error;

      if (units.length === 0) {
        alert('All response units already have embeddings!');
        setIsGenerating(false);
        return;
      }

      // Generate embeddings with progress callback
      const results = await embeddingService.batchGenerateEmbeddings(
        units,
        (progressUpdate) => {
          setProgress(progressUpdate);
        }
      );

      alert(
        `Embedding generation complete!\n\n` +
        `✅ Success: ${results.success}\n` +
        `❌ Failed: ${results.failed}\n` +
        `💰 Total cost: $${results.totalCost.toFixed(4)}`
      );

      await loadStats();
    } catch (error) {
      console.error('Error generating embeddings:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setProgress(null);
    }
  };

  const handleTestHybridSearch = async () => {
    if (!testQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    setIsTesting(true);
    setTestResults(null);

    try {
      const result = await quickHybridSearch(
        testQuery,
        'objection',
        {
          persona: 'CONSULTATIVE',
          stage: 'discovery',
          industry: 'tech'
        }
      );

      setTestResults(result);
    } catch (error) {
      console.error('Error testing hybrid search:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleRefreshStats = async () => {
    try {
      // Refresh materialized view
      await supabase.rpc('refresh_response_performance_stats');
      alert('Performance stats refreshed!');
      await loadPerformanceStats();
    } catch (error) {
      console.error('Error refreshing stats:', error);
      alert(`Error: ${error.message}`);
    }
  };

  if (authLoading) return <LoadingState message="Loading..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white">
      <NavigationBar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24 md:pb-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">🧠 Embeddings & Hybrid Retrieval Admin</h1>
          <p className="text-gray-300">
            Manage embeddings, test hybrid retrieval, and monitor performance
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-1">{stats.totalUnits}</div>
              <div className="text-sm text-gray-300">Total Response Units</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-1">{stats.coverage}%</div>
              <div className="text-sm text-gray-300">Embedding Coverage</div>
              <div className="text-xs text-gray-400 mt-1">
                {stats.unitsWithEmbeddings} / {stats.totalUnits}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-1">{stats.ruleCount}</div>
              <div className="text-sm text-gray-300">Quality Rules</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-3xl font-bold mb-1">{stats.outcomeCount}</div>
              <div className="text-sm text-gray-300">Tracked Outcomes</div>
            </div>
          </div>
        )}

        {/* Embedding Generation */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Generate Embeddings</h2>
          <p className="text-gray-300 mb-4">
            Generate OpenAI embeddings for response units. This enables semantic search
            in the hybrid retrieval system.
          </p>
          
          {progress && (
            <div className="mb-4 p-4 bg-blue-900/50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span>Progress: {progress.current} / {progress.total}</span>
                <span>{Math.round((progress.current / progress.total) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              {progress.status === 'failed' && (
                <div className="text-red-400 text-sm mt-2">
                  Failed: {progress.responseUnitId} - {progress.error}
                </div>
              )}
            </div>
          )}

          <Button
            onClick={handleGenerateEmbeddings}
            disabled={isGenerating || stats?.coverage === 100}
            className="w-full md:w-auto"
          >
            {isGenerating ? 'Generating...' : 'Generate Embeddings'}
          </Button>
        </div>

        {/* Test Hybrid Search */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Test Hybrid Retrieval</h2>
          <p className="text-gray-300 mb-4">
            Test the hybrid retrieval system with a sample query. Combines semantic search,
            rule compliance, confidence scoring, and performance data.
          </p>

          <div className="space-y-4">
            <input
              type="text"
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              placeholder="Enter objection or query (e.g., 'Your price is too high')"
              className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <Button
              onClick={handleTestHybridSearch}
              disabled={isTesting || !testQuery.trim()}
              className="w-full md:w-auto"
            >
              {isTesting ? 'Searching...' : 'Test Search'}
            </Button>

            {testResults && (
              <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
                <div className="mb-4">
                  <div className="text-sm text-gray-400 mb-2">Retrieved via: {testResults.retrievalMethod}</div>
                  <div className="text-2xl font-bold mb-2">{testResults.responseUnit.title}</div>
                  <div className="text-sm text-gray-300">{testResults.responseUnit.type}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <div>
                    <div className="text-xs text-gray-400">Combined</div>
                    <div className="text-xl font-bold">{testResults.scores.combined}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Semantic</div>
                    <div className="text-xl">{testResults.scores.semantic}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Rules</div>
                    <div className="text-xl">{testResults.scores.rule}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Performance</div>
                    <div className="text-xl">{testResults.scores.performance}</div>
                  </div>
                </div>

                <details className="text-sm">
                  <summary className="cursor-pointer text-blue-400 mb-2">View Content</summary>
                  <pre className="bg-black/30 p-4 rounded overflow-auto text-xs">
                    {JSON.stringify(testResults.responseUnit.phases, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>

        {/* Performance Stats */}
        {performanceStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">🏆 Top Performers</h2>
              <p className="text-sm text-gray-300 mb-4">
                Highest success rates (min 3 uses)
              </p>
              {performanceStats.topPerformers.length === 0 ? (
                <p className="text-gray-400">Not enough data yet</p>
              ) : (
                <div className="space-y-3">
                  {performanceStats.topPerformers.map((perf, idx) => (
                    <div key={idx} className="p-3 bg-green-900/30 rounded">
                      <div className="font-semibold">{perf.title}</div>
                      <div className="text-sm text-gray-300">
                        Success Rate: {perf.success_rate}% ({perf.total_uses} uses)
                      </div>
                      <div className="text-xs text-gray-400">
                        Avg Score: {Math.round(perf.avg_score)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">⚠️ Low Performers</h2>
              <p className="text-sm text-gray-300 mb-4">
                Need review (≤50% success, min 3 uses)
              </p>
              {performanceStats.lowPerformers.length === 0 ? (
                <p className="text-gray-400">No low performers found</p>
              ) : (
                <div className="space-y-3">
                  {performanceStats.lowPerformers.map((perf, idx) => (
                    <div key={idx} className="p-3 bg-red-900/30 rounded">
                      <div className="font-semibold">{perf.title}</div>
                      <div className="text-sm text-gray-300">
                        Success Rate: {perf.success_rate}% ({perf.total_uses} uses)
                      </div>
                      <div className="text-xs text-gray-400">
                        Avg Score: {Math.round(perf.avg_score)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8">
          <Button onClick={handleRefreshStats} variant="outline">
            Refresh Performance Stats
          </Button>
        </div>
      </div></div>
  );
}
